import { request, getToken } from '@/utils/request'

// 传记信息接口
export interface Biography {
  id: number // 传记ID
  title: string // 传记标题
  subject_background: string | null // 主人公背景信息
  description: string // 传记描述
  requirements: string // 创作要求
  client_id: number // 客户ID
  writer_id: number // 作者ID
  is_completed: boolean // 是否完成
  created_at: string // 创建时间
  updated_at: string // 更新时间
  status?: 'recent' | 'progress' | 'finished' // 状态：最近、进行中、已完成
  words?: number // 字数
  cover_file_key?: string // 封面文件key
  access_code?: string // 访问码
  client?: {
    id: number // 委托人ID
    name: string // 委托人名称
    phone: string // 委托人电话
  }
}

// 创建传记参数接口
export interface CreateBiographyParams {
  title: string // 传记标题
  client_background: string // 委托人背景
  description: string // 传记描述
  client_id: number // 委托人ID
}

// 创建传记响应接口
export interface CreateBiographyResponse {
  id: number // 传记ID
  title: string // 传记标题
  client_background: string | null // 委托人背景信息
  description: string // 传记描述
  requirements: string // 创作要求
  client_id: number // 委托人ID
  writer_id: number // 作者ID
  is_completed: boolean // 是否完成
  created_at: string // 创建时间
  updated_at: string // 更新时间
}

// 获取传记列表参数接口
export interface GetBiographiesParams {
  page?: number // 页码
  page_size?: number // 每页数量
  search?: string // 搜索关键词
}

/**
 * 获取传记列表
 * @param params 分页参数
 * @returns 传记列表
 */
export const getBiographies = async (params: GetBiographiesParams = {}) => {
  const { page = 1, page_size = 50, search } = params

  return request<ResponseItemsDto<Biography>>({
    url: '/v1/biographies',
    method: 'GET',
    query: {
      page: String(page),
      page_size: String(page_size),
      ...(search && { search })
    }
  })
}

/**
 * 获取传记详情
 * @param biographyId 传记ID
 * @returns 传记详细信息
 */
export const getBiographyById = async (biographyId: number) => {
  return request<Biography>({
    url: `/v1/biographies/${biographyId}`,
    method: 'GET'
  })
}

/**
 * 创建传记
 * @param params 创建传记的参数
 * @returns 创建成功的传记信息
 */
export const createBiography = async (params: CreateBiographyParams) => {
  return request<CreateBiographyResponse>({
    url: '/v1/biographies',
    method: 'POST',
    body: params
  })
}

// 更新传记参数接口
export interface UpdateBiographyParams {
  title?: string // 传记标题
  client_id?: number // 委托人ID
  subject_background?: string // 主人公背景
  description?: string // 传记描述
  is_completed?: boolean // 是否完成
  access_code?: string // 访问码
  cover_file_key?: string // 封面文件key
}

/**
 * 更新传记
 * @param biographyId 传记ID
 * @param params 更新传记的参数
 * @returns 更新后的传记信息
 */
export const updateBiography = async (biographyId: number, params: UpdateBiographyParams) => {
  return request<Biography>({
    url: `/v1/biographies/${biographyId}`,
    method: 'PUT',
    body: params
  })
}

// 创建草稿参数接口
export interface CreateDraftParams {
  biography_id: number // 传记ID
  name: string // 草稿名称
  description: string // 创作要求
}

// 创建草稿响应接口
export interface CreateDraftResponse {
  id: number // 草稿ID
  biography_id: number // 传记ID
  name: string // 草稿名称
  version: number // 版本号
  created_at: string // 创建时间
  updated_at: string // 更新时间
}

/**
 * 创建草稿
 * @param params 创建草稿的参数
 * @returns 创建成功的草稿信息
 */
export const createDraft = async (params: CreateDraftParams) => {
  return request<CreateDraftResponse>({
    url: '/v1/drafts',
    method: 'POST',
    body: params
  })
}

// 草稿信息接口
export interface Draft {
  id: number // 草稿ID
  biography_id: number // 传记ID
  name: string // 草稿名称
  version: number // 版本号
  chapter_count: number // 章节数量
  total_word_count: number // 总字数
  created_at: string // 创建时间
  updated_at: string // 更新时间
  description: string // 创作要求
  outline_status?: 'IDLE' | 'GENERATING' | 'COMPLETED' | 'FAILED' // 大纲生成状态
  is_final?: boolean // 是否为最终版本
}

// 获取草稿列表参数接口
export interface GetDraftsParams {
  biography_id: number // 传记ID
  page?: number // 页码
  page_size?: number // 每页数量
}

/**
 * 获取草稿列表
 * @param params 查询参数
 * @returns 草稿列表
 */
export const getDrafts = async (params: GetDraftsParams) => {
  const { biography_id, page = 1, page_size = 50 } = params

  return request<ResponseItemsDto<Draft>>({
    url: '/v1/drafts',
    method: 'GET',
    query: {
      biography_id: String(biography_id),
      page: String(page),
      page_size: String(page_size)
    }
  })
}

// 草稿详情接口
export interface DraftDetail {
  id: number // 草稿ID
  biography_id: number // 传记ID
  name: string // 草稿名称
  version: number // 版本号
  description: string // 创作要求
  created_at: string // 创建时间
  updated_at: string // 更新时间
  outline_status: 'IDLE' | 'GENERATING' | 'COMPLETED' | 'FAILED' // 大纲生成状态
  outline_requirements?: string // 大纲生成要求
  is_final?: boolean // 是否为最终版本
}

/**
 * 获取草稿详情
 * @param draftId 草稿ID
 * @returns 草稿详细信息
 */
export const getDraftById = async (draftId: number) => {
  return request<DraftDetail>({
    url: `/v1/drafts/${draftId}`,
    method: 'GET'
  })
}

/**
 * 删除草稿
 * @param draftId 草稿ID
 * @returns 删除结果
 */
export const deleteDraft = async (draftId: number) => {
  return request<void>({
    url: `/v1/drafts/${draftId}`,
    method: 'DELETE'
  })
}

// 更新草稿参数接口
export interface UpdateDraftParams {
  name: string // 草稿名称
  description: string // 创作要求
}

/**
 * 更新草稿配置
 * @param draftId 草稿ID
 * @param params 更新草稿的参数
 * @returns 更新后的草稿信息
 */
export const updateDraft = async (draftId: number, params: UpdateDraftParams) => {
  return request<DraftDetail>({
    url: `/v1/drafts/${draftId}`,
    method: 'PUT',
    body: params
  })
}

// 生成草稿大纲参数接口
export interface GenerateDraftOutlineParams {
  requirements: string // 生成要求
}

/**
 * 生成草稿大纲
 * @param draftId 草稿ID
 * @param params 生成要求参数
 * @returns 生成结果
 */
export const generateDraftOutline = async (draftId: number, params: GenerateDraftOutlineParams) => {
  return request<void>({
    url: `/v1/drafts/${draftId}/outline-generations`,
    method: 'POST',
    body: params
  })
}

// 章节信息接口
export interface ChapterData {
  id: number // 章节ID
  draft_id: number // 草稿ID
  chapter_number: number // 章节序号
  title: string // 章节标题
  outline: string // 章节大纲
  content: string // 章节内容
  word_count: number // 字数
  is_completed: boolean // 是否完成
  biography_id: number // 传记ID
  biography_title: string // 传记标题
  draft_version: number // 版本号
  created_at: string // 创建时间
  updated_at: string // 更新时间
  content_status: ContentGenerationStatus
  final_content_version: number // 最终内容版本号
  content_requirements: string // 内容生成要求
}

// 创建章节参数接口
export interface CreateChapterParams {
  draft_id: number // 草稿ID
  chapter_number: number // 章节序号
  title: string // 章节标题
  outline: string // 章节大纲
}

/**
 * 创建章节
 * @param params 创建章节的参数
 * @returns 创建成功的章节信息
 */
export const createChapter = async (params: CreateChapterParams) => {
  return request<ChapterData>({
    url: '/v1/chapters',
    method: 'POST',
    body: params
  })
}

// 获取章节列表参数接口
export interface GetChaptersParams {
  draft_id: number // 草稿ID
  page?: number // 页码
  page_size?: number // 每页数量
}

/**
 * 获取章节列表
 * @param params 查询参数
 * @returns 章节列表
 */
export const getChapters = async (params: GetChaptersParams) => {
  const { draft_id, page = 1, page_size = 50 } = params

  return request<ResponseItemsDto<ChapterData>>({
    url: '/v1/chapters',
    method: 'GET',
    query: {
      draft_id: String(draft_id),
      page: String(page),
      page_size: String(page_size)
    }
  })
}

/**
 * 获取章节详情
 * @param chapterId 章节ID
 * @returns 章节详细信息
 */
export const getChapterById = async (chapterId: number) => {
  return request<ChapterData>({
    url: `/v1/chapters/${chapterId}`,
    method: 'GET'
  })
}

// 更新章节参数接口
export interface UpdateChapterParams {
  chapter_number: number // 章节序号
  title: string // 章节标题
  outline: string // 章节大纲
  is_completed: boolean // 是否完成
}

/**
 * 更新章节
 * @param chapterId 章节ID
 * @param params 更新章节的参数
 * @returns 更新后的章节信息
 */
export const updateChapter = async (chapterId: number, params: UpdateChapterParams) => {
  return request<ChapterData>({
    url: `/v1/chapters/${chapterId}`,
    method: 'PUT',
    body: params
  })
}

/**
 * 删除章节
 * @param chapterId 章节ID
 * @returns 删除结果
 */
export const deleteChapter = async (chapterId: number) => {
  return request<void>({
    url: `/v1/chapters/${chapterId}`,
    method: 'DELETE'
  })
}

// 获取文档上传URL参数接口
export interface GetUploadUrlParams {
  filename: string // 文件名
}

// 获取文档上传URL响应接口
export interface GetUploadUrlResponse {
  upload_url: string // 上传URL
  file_key: string // 文件key
  content_type: string // 内容类型
  expires_in: number // 过期时间（秒）
}

/**
 * 获取文档上传URL
 * @param params 包含文件名的参数
 * @returns 上传URL信息
 */
export const getDocumentUploadUrl = async (params: GetUploadUrlParams & { type: 'document' | 'image' | 'audio' }) => {
  return request<GetUploadUrlResponse>({
    url: '/v1/files/upload-urls',
    method: 'GET',
    query: {
      filename: params.filename,
      file_type: params.type
    }
  })
}

// 提取文档内容参数接口
export interface GetDocumentContentExtractionParams {
  biography_id: number // 传记ID
  file_key: string // 文件key
}

// 提取文档内容响应接口
export interface GetDocumentContentExtractionResponse {
  biography_id: number // 传记ID
  file_key: string // 文件key
  summary: string // 文档摘要
  tags: string[] // 标签列表
}

/**
 * 提取文档内容
 * @param params 包含传记ID和文件key的参数
 * @returns 提取的文档内容信息
 */
export const getDocumentContentExtraction = async (params: GetDocumentContentExtractionParams) => {
  return request<GetDocumentContentExtractionResponse>({
    url: '/v1/documents/summary-generations',
    method: 'POST',
    body: params
  })
}

// 检查文档关联度参数接口
export interface CheckDocumentUsefulnessParams {
  biography_id: number // 传记ID
  file_key: string // 文件key
}

// 检查文档关联度响应接口
export interface CheckDocumentUsefulnessResponse {
  usefulness: 'high' | 'medium' | 'low' // 关联度
  reason: string // 不关联的理由（当 usefulness 为 low 时）
}

/**
 * 检查文档与传记主题的关联度
 * @param params 包含传记ID和文件key的参数
 * @returns 文档关联度信息
 */
export const checkDocumentUsefulness = async (params: CheckDocumentUsefulnessParams) => {
  return request<CheckDocumentUsefulnessResponse>({
    url: '/v1/documents/usefulness-detection',
    method: 'POST',
    body: params
  })
}

// 创建文档参数接口
export interface CreateDocumentParams {
  biography_id: number // 传记ID
  name: string // 文档名称
  summary: string // 文档摘要
  description: string // 文档描述
  tags: string[] // 标签列表
  file_key: string // 文件key
}

// 创建文档响应接口
export interface CreateDocumentResponse {
  id: number // 文档ID
  biography_id: number // 传记ID
  name: string // 文档名称
  summary: string // 文档摘要
  description: string // 文档描述
  tags: string[] // 标签列表
  file_key: string // 文件key
  created_at: string // 创建时间
  updated_at: string // 更新时间
  analysis_status: AnalysisStatus // 分析状态
  analyzed_at: string // 分析时间
}

/**
 * 创建文档
 * @param params 创建文档的参数
 * @returns 创建成功的文档信息
 */
export const createDocument = async (params: CreateDocumentParams) => {
  return request<CreateDocumentResponse>({
    url: '/v1/documents',
    method: 'POST',
    body: params
  })
}

// 文档信息接口
export interface DocumentItem {
  id: number // 文档ID
  biography_id: number // 传记ID
  name: string // 文档名称
  tags: string[] // 标签列表
  file_key: string // 文件key
  memory_status: AnalysisStatus // 语义分析状态
  character_status: AnalysisStatus // 关系分析状态
  created_at: string // 创建时间
  updated_at: string // 更新时间
  summary: string // 文档摘要
}

// 获取文档列表参数接口
export interface GetDocumentsParams {
  biography_id: number // 传记ID
  search?: string // 搜索关键词
  page?: number // 页码
  page_size?: number // 每页数量
}

/**
 * 获取文档列表
 * @param params 查询参数
 * @returns 文档列表
 */
export const getDocuments = async (params: GetDocumentsParams) => {
  const { biography_id, search, page = 1, page_size = 50 } = params
  
  return request<ResponseItemsDto<DocumentItem>>({
    url: '/v1/documents',
    method: 'GET',
    query: {
      biography_id: String(biography_id),
      ...(search && { search }),
      page: String(page),
      page_size: String(page_size)
    }
  })
}

// 文档人物信息接口
export interface DocumentCharacter {
  name: string // 人物姓名
  aliases: string[] // 别名列表
  connections: string // 人物关系描述
  description: string // 人物描述
}

// 文档详情接口
export interface DocumentDetail {
  id: number // 文档ID
  biography_id: number // 传记ID
  name: string // 文档名称
  description: string // 文档描述
  summary: string // 文档摘要
  tags: string[] // 标签列表
  content: string // 文档内容
  file_key: string // 文件key
  size: number // 文件大小（字节）
  memory_status: string // 语义分析状态
  character_status: string // 人物关系分析状态
  characters?: DocumentCharacter[] // 人物关系列表
  analyzed_at: string // 分析时间
  created_at: string // 创建时间
  updated_at: string // 更新时间
}

/**
 * 获取文档详情
 * @param documentId 文档ID
 * @returns 文档详细信息
 */
export const getDocumentById = async (documentId: number) => {
  return request<DocumentDetail>({
    url: `/v1/documents/${documentId}`,
    method: 'GET'
  })
}

// 更新文档参数接口
export interface UpdateDocumentParams {
  name: string // 文档名称
  description: string // 文档描述
  summary: string // 文档摘要
  tags: string[] // 标签列表
  content: string // 文档内容
}

/**
 * 更新文档
 * @param documentId 文档ID
 * @param params 更新文档的参数
 * @returns 更新后的文档信息
 */
export const updateDocument = async (documentId: number, params: UpdateDocumentParams) => {
  return request<DocumentDetail>({
    url: `/v1/documents/${documentId}`,
    method: 'PUT',
    body: params
  })
}

/**
 * 删除文档
 * @param documentId 文档ID
 * @returns 删除结果
 */
export const deleteDocument = async (documentId: number) => {
  return request<void>({
    url: `/v1/documents/${documentId}`,
    method: 'DELETE'
  })
}

// Memory 生成状态
export type MemoryGenerationStatus = 'processing' | 'IDLE' | 'GENERATING' | 'COMPLETED' | 'FAILED'

export type AnalysisStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'

export type ContentGenerationStatus = 'IDLE' | 'GENERATING' | 'COMPLETED' | 'FAILED'

// 生成 Memory 响应接口
export interface GenerateMemoryResponse {
  document_id: number // 文档ID
  message: string // 提示信息
  status: MemoryGenerationStatus // 生成状态
}

/**
 * 生成文档 Memory
 * @param documentId 文档ID
 * @returns 生成任务信息
 */
export const generateDocumentMemory = async (documentId: number) => {
  return request<GenerateMemoryResponse>({
    url: `/v1/documents/${documentId}/memory-generations`,
    method: 'POST'
  })
}

/**
 * 提取文档人物关系
 * @param documentId 文档ID
 * @returns 提取任务信息
 */
export const extractDocumentCharacters = async (documentId: number) => {
  return request<void>({
    url: `/v1/documents/${documentId}/character-extractions`,
    method: 'POST'
  })
}

// 文档 Memory 项接口
export interface DocumentMemoryItem {
  id: number // Memory ID
  source_id: number // 文档ID
  source_type: string // 来源类型
  brief: string // 描述/摘要
  content: string // 内容
  created_at: string // 创建时间
}

// 获取文档 Memories 参数接口
export interface GetDocumentMemoriesParams {
  page?: number // 页码
  page_size?: number // 每页数量
}

// 获取文档 Memories 响应接口
export interface GetDocumentMemoriesResponse {
  items: DocumentMemoryItem[] // Memory 列表
  meta: {
    total: number // 总数
    page_size: number // 每页数量
    page: number // 当前页码
  }
}

/**
 * 获取文档 Memories
 * @param documentId 文档ID
 * @param params 查询参数
 * @returns Memories 列表
 */
export const getDocumentMemories = async (
  documentId: number,
  params: GetDocumentMemoriesParams = {}
) => {
  return request<GetDocumentMemoriesResponse>({
    url: `/v1/documents/${documentId}/memories`,
    method: 'GET',
    query: {
      page: String(params.page || 1),
      page_size: String(params.page_size || 50)
    }
  })
}

// 创建图片参数接口
export interface CreateImageParams {
  biography_id: number // 传记ID
  file_key: string // 文件key
  caption: string // 图片标题
  description: string // 图片描述
}

// 创建图片响应接口
export interface CreateImageResponse {
  id: number // 图片ID
  biography_id: number // 传记ID
  file_key: string // 文件key
  caption: string // 图片标题
  description: string // 图片描述
  created_at: string // 创建时间
  updated_at: string // 更新时间
}

/**
 * 创建图片（将图片绑定到传记）
 * @param params 创建图片的参数
 * @returns 创建成功的图片信息
 */
export const createImage = async (params: CreateImageParams) => {
  return request<CreateImageResponse>({
    url: '/v1/images',
    method: 'POST',
    body: params
  })
}

// 图片信息接口
export interface ImageItem {
  id: number // 图片ID
  biography_id: number // 传记ID
  file_key: string // 文件key
  caption: string // 图片标题
  description: string // 图片描述
  created_at: string // 创建时间
  updated_at: string // 更新时间
}

// 获取图片列表参数接口
export interface GetImagesParams {
  biography_id: number // 传记ID
  search?: string // 搜索关键词
  page?: number // 页码
  page_size?: number // 每页数量
}

/**
 * 获取图片列表
 * @param params 查询参数
 * @returns 图片列表
 */
export const getImages = async (params: GetImagesParams) => {
  const { biography_id, search, page = 1, page_size = 50 } = params

  return request<ResponseItemsDto<ImageItem>>({
    url: '/v1/images',
    method: 'GET',
    query: {
      biography_id: String(biography_id),
      ...(search && { search }),
      page: String(page),
      page_size: String(page_size)
    }
  })
}

// 图片详情接口
export interface ImageDetail {
  id: number // 图片ID
  biography_id: number // 传记ID
  file_key: string // 文件key
  caption: string // 图片标题
  description: string // 图片描述
  created_at: string // 创建时间
  updated_at: string // 更新时间
}

/**
 * 获取图片详情
 * @param imageId 图片ID
 * @returns 图片详细信息
 */
export const getImageById = async (imageId: number) => {
  return request<ImageDetail>({
    url: `/v1/images/${imageId}`,
    method: 'GET'
  })
}

// 获取文件下载URL响应接口
export interface GetFileDownloadUrlResponse {
  download_url: string // 下载URL
  expires_in: number // 过期时间（秒）
  file_key: string // 文件key
}

/**
 * 获取文件下载URL
 * @param fileKey 文件key
 * @returns 文件下载URL信息
 */
export const getFileDownloadUrl = async (fileKey: string) => {
  return request<GetFileDownloadUrlResponse>({
    url: '/v1/files/download-urls',
    method: 'GET',
    query: {
      file_key: fileKey
    }
  })
}

// 更新图片参数接口
export interface UpdateImageParams {
  caption: string // 图片标题
  description: string // 图片描述
}

/**
 * 更新图片信息
 * @param imageId 图片ID
 * @param params 更新参数
 * @returns 更新后的图片信息
 */
export const updateImage = async (imageId: number, params: UpdateImageParams) => {
  return request<ImageDetail>({
    url: `/v1/images/${imageId}`,
    method: 'PUT',
    body: params
  })
}

/**
 * 删除图片
 * @param imageId 图片ID
 */
export const deleteImage = async (imageId: number) => {
  return request<void>({
    url: `/v1/images/${imageId}`,
    method: 'DELETE'
  })
}

// 生成章节内容参数接口
export interface GenerateChapterContentParams {
  requirements: string // 生成要求
}

/**
 * 生成章节内容
 * @param chapterId 章节ID
 * @param params 生成参数
 */
export const generateChapterContent = async (chapterId: number, params: GenerateChapterContentParams) => {
  return request<void>({
    url: `/v1/chapters/${chapterId}/content-generations`,
    method: 'POST',
    body: params
  })
}

// 更新章节顺序参数接口
export interface UpdateChapterOrderParams {
  chapter_ids: number[] // 章节ID数组，按新的顺序排列
}

/**
 * 更新章节顺序
 * @param draftId 草稿ID
 * @param params 章节ID数组，按新的顺序排列
 */
export const updateChapterOrder = async (draftId: number, params: UpdateChapterOrderParams) => {
  return request<void>({
    url: `/v1/drafts/${draftId}/chapter-order`,
    method: 'PUT',
    body: params
  })
}

// 章节内容版本接口
export interface ChapterContentVersion {
  id: number // 内容版本ID
  chapter_id: number // 章节ID
  version: number // 版本号
  is_final: boolean // 是否为最终版本
  word_count: number // 字数
  created_at: string // 创建时间
  updated_at: string // 更新时间
}

/**
 * 获取章节内容版本列表
 * @param chapterId 章节ID
 * @returns 章节内容版本列表
 */
export const getChapterContentVersions = async (chapterId: number) => {
  return request<ChapterContentVersion[]>({
    url: `/v1/chapters/${chapterId}/contents`,
    method: 'GET'
  })
}

// 章节内容详情接口
export interface ChapterContentDetail {
  id: number // 内容版本ID
  chapter_id: number // 章节ID
  version: number // 版本号
  is_final: boolean // 是否为最终版本
  content: string // 内容
  word_count: number // 字数
  created_at: string // 创建时间
  updated_at: string // 更新时间
}

/**
 * 获取章节版本内容
 * @param chapterId 章节ID
 * @param version 版本号
 * @returns 章节版本内容详情
 */
export const getChapterContentByVersion = async (chapterId: number, version: number) => {
  return request<ChapterContentDetail>({
    url: `/v1/chapters/${chapterId}/contents/${version}`,
    method: 'GET'
  })
}

// 保存章节内容参数接口
export interface SaveChapterContentParams {
  content: string // 章节内容
}

// 创建章节内容参数接口
export interface CreateChapterContentParams {
  content: string // 章节内容
}

/**
 * 创建章节内容
 * @param chapterId 章节ID
 * @param params 创建章节内容的参数
 * @returns 创建后的章节内容详情
 */
export const createChapterContent = async (
  chapterId: number,
  params: CreateChapterContentParams
) => {
  return request<ChapterContentDetail>({
    url: `/v1/chapters/${chapterId}/contents`,
    method: 'POST',
    body: params
  })
}

/**
 * 保存章节内容
 * @param chapterId 章节ID
 * @param version 版本号
 * @param params 保存章节内容的参数
 * @returns 保存后的章节内容详情
 */
export const saveChapterContent = async (
  chapterId: number,
  version: number,
  params: SaveChapterContentParams
) => {
  return request<ChapterContentDetail>({
    url: `/v1/chapters/${chapterId}/contents/${version}`,
    method: 'PUT',
    body: params
  })
}

// 设置活动章节内容版本参数接口
export interface SetFinalChapterContentParams {
  version: number // 版本号
}

/**
 * 设置活动章节内容版本
 * @param chapterId 章节ID
 * @param params 版本号参数
 */
export const setFinalChapterContent = async (
  chapterId: number,
  params: SetFinalChapterContentParams
) => {
  return request({
    url: `/v1/chapters/${chapterId}/final-content`,
    method: 'PUT',
    body: params
  })
}

// 导出草稿文档响应接口
export interface ExportDraftDocumentResponse {
  file_key: string // 文件key
  download_url: string // 下载URL
  filename: string // 文件名
  draft_id: number // 草稿ID
}

/**
 * 导出草稿为 DOCX
 * @param draftId 草稿ID
 * @returns 导出文件信息
 */
export const exportDraftDocument = async (draftId: number) => {
  return request<ExportDraftDocumentResponse>({
    url: `/v1/drafts/${draftId}/docx-exports`,
    method: 'POST'
  })
}

/**
 * 导出草稿大纲
 * @param draftId 草稿ID
 * @returns 导出文件信息
 */
export const exportDraftOutline = async (draftId: number) => {
  return request<ExportDraftDocumentResponse>({
    url: `/v1/drafts/${draftId}/outline-exports`,
    method: 'POST'
  })
}

/**
 * 导出草稿为 PDF
 * @param draftId 草稿ID
 * @returns 导出文件信息
 */
export const exportDraftPdf = async (draftId: number) => {
  return request<ExportDraftDocumentResponse>({
    url: `/v1/drafts/${draftId}/pdf-exports`,
    method: 'POST'
  })
}

/**
 * 设置草稿为最终版本
 * @param draftId 草稿ID
 * @returns 更新后的草稿信息
 */
export const setDraftFinal = async (draftId: number) => {
  return request<DraftDetail>({
    url: `/v1/drafts/${draftId}/final`,
    method: 'PUT'
  })
}

/**
 * 流式章节大纲优化参数
 */
export interface StreamOutlineRefinementParams {
  /** 章节ID */
  chapterId: number
  /** 章节标题（title 和 draftOutline 必须至少传一个） */
  title?: string
  /** 大纲草稿（title 和 draftOutline 必须至少传一个） */
  draftOutline?: string
  /** 用户指令（必传） */
  instruction: string
  /** 文本块回调 */
  onChunk: (text: string) => void
  /** 完成回调 */
  onComplete?: () => void
  /** 错误回调 */
  onError?: (error: Error) => void
  /** 中止信号 */
  signal?: AbortSignal
}

/**
 * 流式章节大纲优化 API
 * 使用 SSE 流式返回优化后的大纲
 *
 * @param params - 流式大纲优化参数
 */
export const refineChapterOutlineStream = async (
  params: StreamOutlineRefinementParams
): Promise<void> => {
  const { chapterId, title, draftOutline, instruction, onChunk, onComplete, onError, signal } = params

  const BASE_URL = import.meta.env.VITE_API_URL + '/api'
  const url = `${BASE_URL}/v1/chapters/${chapterId}/outline-refinements`

  const token = getToken()
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers.Authorization = token
  }

  try {
    const { fetchEventSource } = await import('@microsoft/fetch-event-source')

    // 构建请求体，只传递有值的字段
    const requestBody: Record<string, string> = {
      instruction,
    }
    if (title) {
      requestBody.title = title
    }
    if (draftOutline) {
      requestBody.draft_outline = draftOutline
    }

    await fetchEventSource(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
      signal,
      openWhenHidden: true,

      onopen: async (response) => {
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new Error('认证失败，请重新登录')
          }
          throw new Error(`HTTP error! status: ${response.status}`)
        }
      },

      onmessage: (event) => {
        // SSE 消息格式: data: {"type": "chunk", "text": "..."} 或 data: {"type": "done"}
        try {
          const data = JSON.parse(event.data)

          if (event.event === 'message' && data.text) {
            onChunk(data.text)
          } else if (event.event === 'message_stop') {
            onComplete?.()
          } else if (event.event === 'error') {
            // 处理服务端返回的错误
            const errorMessage = data.error || data.message || 'AI 生成失败'
            const error = new Error(errorMessage)
            onError?.(error)
            throw error // 停止 SSE 连接
          }
        } catch (e) {
          console.error('解析 SSE 消息失败:', e)
          // 如果是我们主动抛出的错误，继续向上传递
          if (e instanceof Error && e.message !== '解析 SSE 消息失败') {
            throw e
          }
        }
      },

      onerror: (error) => {
        console.error('SSE 连接错误:', error)
        // 不调用 onError，因为错误已经在 onmessage 中处理过了
        throw error // 停止重连
      },
    })
  } catch (error) {
    console.error('流式请求失败:', error)
    // onError 已经在 onmessage 中调用过了，这里不再重复调用
    throw error
  }
}

// ==================== Chat Session 相关接口 ====================

/** Chat Session 类型 */
export type ChatSessionType = 'READER' | 'WRITING' | 'INTERVIEW'

/** Chat Session 数据结构 */
export interface ChatSession {
  id: string // Session UUID
  biography_id: number // 传记ID
  name: string // 会话名称
  type: ChatSessionType // 会话类型
  created_at: string // 创建时间
  updated_at: string // 更新时间
}

/** 聊天消息角色 */
export type ChatMessageRole = 'USER' | 'ASSISTANT'

/** 聊天消息数据结构 */
export interface ChatMessage {
  id: number // 消息 ID
  chat_session_id: string // 会话 ID
  role: ChatMessageRole // 消息角色
  content: string // 消息内容
  created_at: string // 创建时间
}

/** 带消息列表的会话详情 */
export interface ChatSessionWithMessages extends ChatSession {
  messages: ChatMessage[] // 消息列表
}

/** 创建 Chat Session 参数 (写作助手) */
export interface CreateChatSessionParams {
  biography_id: number // 传记ID
  name: string // 会话名称
  // type 字段不再需要，后端自动设置为 WRITING
}

/**
 * 创建聊天会话 (写作助手)
 * @param params 创建参数
 * @returns Chat Session 信息
 */
export const createChatSession = async (params: CreateChatSessionParams) => {
  return request<ChatSession>({
    url: '/v1/assistant/chat-sessions',
    method: 'POST',
    body: params
  })
}

/** 获取聊天会话列表参数 */
export interface GetChatSessionsParams {
  biography_id: number // 传记ID
  page?: number // 页码
  page_size?: number // 每页数量
  type?: ChatSessionType // 会话类型（可选，列表接口自动过滤 WRITING）
}

/** 聊天会话列表响应 */
export interface ChatSessionsResponse {
  items: ChatSession[]
  meta: {
    total: number
    page_size: number
    page: number
  }
}

/**
 * 获取聊天会话列表 (写作助手)
 * @param params 查询参数
 * @returns 会话列表
 */
export const getChatSessions = async (params: GetChatSessionsParams) => {
  const { biography_id, page = 1, page_size = 20 } = params

  const query: Record<string, string> = {
    biography_id: String(biography_id),
    page: String(page),
    page_size: String(page_size)
  }

  return request<ChatSessionsResponse>({
    url: '/v1/assistant/chat-sessions',
    method: 'GET',
    query
  })
}

/**
 * 获取会话详情（包含消息列表）
 * @param sessionId 会话ID
 * @returns 会话详情和消息列表
 */
export const getChatSessionDetail = async (sessionId: string) => {
  return request<ChatSessionWithMessages>({
    url: `/v1/assistant/chat-sessions/${sessionId}`,
    method: 'GET'
  })
}

/** 发送聊天消息参数 */
export interface SendChatMessageParams {
  content: string // 消息内容
}

/** 流式发送聊天消息参数 */
export interface StreamChatMessageParams {
  /** Session ID */
  sessionId: string
  /** 消息内容 */
  content: string
  /** 文本块回调 */
  onChunk: (text: string) => void
  /** 完成回调 */
  onComplete?: () => void
  /** 错误回调 */
  onError?: (error: Error) => void
  /** 中止信号 */
  signal?: AbortSignal
}

/**
 * 流式发送聊天消息
 * 使用 SSE 流式返回 AI 回复
 *
 * @param params - 流式聊天参数
 */
export const sendChatMessageStream = async (
  params: StreamChatMessageParams
): Promise<void> => {
  const { sessionId, content, onChunk, onComplete, onError, signal } = params

  const BASE_URL = import.meta.env.VITE_API_URL + '/api'
  const url = `${BASE_URL}/v1/assistant/chat-sessions/${sessionId}/messages`

  const token = getToken()
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers.Authorization = token
  }

  try {
    const { fetchEventSource } = await import('@microsoft/fetch-event-source')

    await fetchEventSource(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ content }),
      signal,
      openWhenHidden: true,

      onopen: async (response) => {
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new Error('认证失败，请重新登录')
          }
          throw new Error(`HTTP error! status: ${response.status}`)
        }
      },

      onmessage: (event) => {
        // SSE 消息格式: data: {"type": "chunk", "text": "..."} 或 data: {"type": "done"}
        try {
          const data = JSON.parse(event.data)

          if (event.event === 'message' && data.text) {
            onChunk(data.text)
          } else if (event.event === 'message_stop') {
            onComplete?.()
          } else if (event.event === 'error') {
            // 处理服务端返回的错误
            const errorMessage = data.error || data.message || 'AI 响应失败'
            const error = new Error(errorMessage)
            onError?.(error)
            throw error // 停止 SSE 连接
          }
        } catch (e) {
          console.error('解析 SSE 消息失败:', e)
          // 如果是我们主动抛出的错误，继续向上传递
          if (e instanceof Error && e.message !== '解析 SSE 消息失败') {
            throw e
          }
        }
      },

      onerror: (error) => {
        console.error('SSE 连接错误:', error)
        throw error // 停止重连
      },
    })
  } catch (error) {
    console.error('流式聊天请求失败:', error)
    throw error
  }
}

// ==================== 采访录音相关 API ====================

// 采访状态类型
export type InterviewStatus = 'PENDING' | 'ACTIVE' | 'COMPLETED'

// 采访录音信息接口
export interface InterviewItem {
  id: number // 采访ID
  chat_session_id: string // 聊天会话ID
  biography_id: number // 传记ID
  name: string // 采访标题
  status: InterviewStatus // 采访状态
  created_at: string // 创建时间
  updated_at: string // 更新时间
}

// 获取采访录音列表参数接口
export interface GetInterviewsParams {
  biography_id: number // 传记ID
  status_filter?: InterviewStatus // 状态筛选
  search?: string // 搜索关键词（搜索名称、描述和摘要）
  page?: number // 页码
  page_size?: number // 每页数量
}

/**
 * 获取采访录音列表
 * @param params 查询参数
 * @returns 采访录音列表
 */
export const getInterviews = async (params: GetInterviewsParams) => {
  const { biography_id, status_filter, search, page = 1, page_size = 50 } = params

  return request<ResponseItemsDto<InterviewItem>>({
    url: '/v1/interviews',
    method: 'GET',
    query: {
      biography_id: String(biography_id),
      ...(status_filter && { status_filter }),
      ...(search && { search }),
      page: String(page),
      page_size: String(page_size)
    }
  })
}

// 采访录音详情接口
export interface InterviewDetail extends InterviewItem {
  description?: string // 采访描述
  summary?: string // 采访摘要
  analysis_status: AnalysisStatus // 分析状态
  analyzed_at?: string // 分析完成时间
  outline?: string // 采访大纲
}

/**
 * 获取采访录音详情
 * @param interviewId 采访ID
 * @returns 采访录音详细信息
 */
export const getInterviewById = async (interviewId: number) => {
  return request<InterviewDetail>({
    url: `/v1/interviews/${interviewId}`,
    method: 'GET'
  })
}

// 创建采访录音参数接口
export interface CreateInterviewParams {
  biography_id: number // 传记ID
  name: string // 采访标题
  description?: string // 采访描述
  outline: string // 采访大纲
}

/**
 * 创建采访录音
 * @param params 创建采访的参数
 * @returns 创建成功的采访信息
 */
export const createInterview = async (params: CreateInterviewParams) => {
  return request<InterviewItem>({
    url: '/v1/interviews',
    method: 'POST',
    body: params
  })
}

// 更新采访录音参数接口
export interface UpdateInterviewParams {
  name?: string // 采访标题
  description?: string // 采访描述
  outline?: string // 采访大纲
  summary?: string // 采访摘要
}

/**
 * 更新采访录音
 * @param interviewId 采访ID
 * @param params 更新采访的参数
 * @returns 更新后的采访信息
 */
export const updateInterview = async (interviewId: number, params: UpdateInterviewParams) => {
  return request<InterviewItem>({
    url: `/v1/interviews/${interviewId}`,
    method: 'PUT',
    body: params
  })
}

/**
 * 删除采访录音
 * @param interviewId 采访ID
 * @returns 删除结果
 */
export const deleteInterview = async (interviewId: number) => {
  return request<void>({
    url: `/v1/interviews/${interviewId}`,
    method: 'DELETE'
  })
}

// ==================== 录音相关 API ====================

// 录音转写状态
export type TranscriptionStatus = 'PENDING' | 'TRANSCRIBING' | 'TRANSCRIBED' | 'FAILED'

// 录音信息接口
export interface RecordingItem {
  id: number // 录音ID
  biography_id: number // 传记ID
  name: string // 录音标题
  description?: string // 录音描述
  file_key: string // 音频文件key（用于列表显示，实际可能是 original_file_key）
  duration?: number // 音频时长（秒）
  file_size?: number // 文件大小（字节）
  created_at: string // 创建时间
  updated_at: string // 更新时间
  transcription_status?: TranscriptionStatus // 转写状态（列表中可选）
  analysis_status?: AnalysisStatus // 分析状态（列表中可选）
}

// 获取录音列表参数接口
export interface GetRecordingsParams {
  biography_id: number // 传记ID
  search?: string // 搜索关键词（搜索名称和描述）
  page?: number // 页码
  page_size?: number // 每页数量
}

/**
 * 获取录音列表
 * @param params 查询参数
 * @returns 录音列表
 */
export const getRecordings = async (params: GetRecordingsParams) => {
  const { biography_id, search, page = 1, page_size = 50 } = params

  return request<ResponseItemsDto<RecordingItem>>({
    url: '/v1/recordings',
    method: 'GET',
    query: {
      biography_id: String(biography_id),
      ...(search && { search }),
      page: String(page),
      page_size: String(page_size)
    }
  })
}

// 录音详情接口
export interface RecordingDetail {
  id: number // 录音ID
  biography_id: number // 传记ID
  name: string // 录音标题
  description?: string // 录音描述
  summary?: string // 摘要
  tags?: string[] // 标签列表
  analysis_status: AnalysisStatus // 分析状态
  analyzed_at?: string // 分析完成时间
  created_at: string // 创建时间
  updated_at: string // 更新时间
  original_file_key: string // 原始音频文件key
  converted_file_key?: string // 转换后的音频文件key
  duration?: number // 音频时长（秒）
  transcript?: string // 转写文本
  transcription_status: TranscriptionStatus // 转写状态
}

/**
 * 获取录音详情
 * @param recordingId 录音ID
 * @returns 录音详细信息
 */
export const getRecordingById = async (recordingId: number) => {
  return request<RecordingDetail>({
    url: `/v1/recordings/${recordingId}`,
    method: 'GET'
  })
}

// 创建录音参数接口
export interface CreateRecordingParams {
  biography_id: number // 传记ID
  name: string // 录音标题
  description?: string // 录音描述
  original_file_key: string // 原始音频文件key
}

/**
 * 创建录音
 * @param params 创建录音的参数
 * @returns 创建成功的录音信息
 */
export const createRecording = async (params: CreateRecordingParams) => {
  return request<RecordingItem>({
    url: '/v1/recordings',
    method: 'POST',
    body: params
  })
}

// 更新录音参数接口
export interface UpdateRecordingParams {
  name?: string // 录音标题
  description?: string // 录音描述
  summary?: string // 录音摘要
}

/**
 * 更新录音
 * @param recordingId 录音ID
 * @param params 更新录音的参数
 * @returns 更新后的录音信息
 */
export const updateRecording = async (recordingId: number, params: UpdateRecordingParams) => {
  return request<RecordingItem>({
    url: `/v1/recordings/${recordingId}`,
    method: 'PUT',
    body: params
  })
}

/**
 * 删除录音
 * @param recordingId 录音ID
 * @returns 删除结果
 */
export const deleteRecording = async (recordingId: number) => {
  return request<void>({
    url: `/v1/recordings/${recordingId}`,
    method: 'DELETE'
  })
}

/**
 * 获取音频文件上传URL
 * @param params 包含文件名的参数
 * @returns 上传URL信息
 */
export const getAudioUploadUrl = async (params: GetUploadUrlParams) => {
  return request<GetUploadUrlResponse>({
    url: '/v1/files/upload-urls',
    method: 'GET',
    query: {
      filename: params.filename,
      file_type: 'audio'
    }
  })
}

/**
 * 重新生成采访摘要
 * @param interviewId 采访ID
 * @returns 生成任务信息
 */
export const regenerateInterviewSummary = async (interviewId: number) => {
  return request<void>({
    url: `/v1/interviews/${interviewId}/summary-generations`,
    method: 'POST'
  })
}

/**
 * 生成采访 Memory（语义分析）
 * @param interviewId 采访ID
 * @returns 生成任务信息
 */
export const generateInterviewMemory = async (interviewId: number) => {
  return request<GenerateMemoryResponse>({
    url: `/v1/interviews/${interviewId}/memory-generations`,
    method: 'POST'
  })
}

// 采访 Memory 项接口
export interface InterviewMemoryItem {
  id: number // Memory ID
  source_id: number // 采访ID
  brief: string // 摘要
  created_at: string // 创建时间
}

// 获取采访 Memories 参数接口
export interface GetInterviewMemoriesParams {
  page?: number // 页码
  page_size?: number // 每页数量
}

/**
 * 获取采访 Memories（语义分析结果）
 * @param interviewId 采访ID
 * @param params 查询参数
 * @returns Memories 列表
 */
export const getInterviewMemories = async (
  interviewId: number,
  params: GetInterviewMemoriesParams = {}
) => {
  return request<GetDocumentMemoriesResponse>({
    url: `/v1/interviews/${interviewId}/memories`,
    method: 'GET',
    query: {
      page: String(params.page || 1),
      page_size: String(params.page_size || 50)
    }
  })
}

/**
 * 重新生成录音摘要
 * @param recordingId 录音ID
 * @returns 生成任务信息
 */
export const regenerateRecordingSummary = async (recordingId: number) => {
  return request<void>({
    url: `/v1/recordings/${recordingId}/summary-generations`,
    method: 'POST'
  })
}

/**
 * 生成录音 Memory（语义分析）
 * @param recordingId 录音ID
 * @returns 生成任务信息
 */
export const generateRecordingMemory = async (recordingId: number) => {
  return request<GenerateMemoryResponse>({
    url: `/v1/recordings/${recordingId}/memory-generations`,
    method: 'POST'
  })
}

/**
 * 获取录音 Memories（语义分析结果）
 * @param recordingId 录音ID
 * @param params 查询参数
 * @returns Memories 列表
 */
export const getRecordingMemories = async (
  recordingId: number,
  params: GetInterviewMemoriesParams = {}
) => {
  return request<GetDocumentMemoriesResponse>({
    url: `/v1/recordings/${recordingId}/memories`,
    method: 'GET',
    query: {
      page: String(params.page || 1),
      page_size: String(params.page_size || 50)
    }
  })
}

// ==================== Biography Memory 相关 API ====================

// 传记 Memory 项接口
export interface BiographyMemoryItem {
  id: number // Memory ID
  source_id: number // 来源ID
  source_type: 'DOCUMENT' | 'INTERVIEW' | 'RECORDING' | 'MANUAL' // 来源类型（大写）
  brief: string // 简要描述
  content: string // 内容
  created_at: string // 创建时间
}

// 获取 Memories 参数接口
export interface GetMemoriesParams {
  biography_id: number // 传记ID（必填）
  search?: string // 搜索关键词
  page?: number // 页码，默认 1
  page_size?: number // 每页数量，默认 50
}

// 获取 Memories 响应接口
export interface GetMemoriesResponse {
  items: BiographyMemoryItem[]
  meta: {
    total: number
    page_size: number
    page: number
  }
}

/**
 * 获取 Memories 列表
 * @param params 查询参数
 * @returns Memories 列表
 */
export const getMemories = async (params: GetMemoriesParams) => {
  const { biography_id, search, page = 1, page_size = 50 } = params

  return request<GetMemoriesResponse>({
    url: '/v1/memories',
    method: 'GET',
    query: {
      biography_id: String(biography_id),
      ...(search && { search }),
      page: String(page),
      page_size: String(page_size)
    }
  })
}

// 创建 Memory 参数接口
export interface CreateMemoryParams {
  biography_id: number // 传记ID
  brief: string // 简要描述
  content: string // 内容
}

/**
 * 创建 Memory
 * @param params 创建参数
 * @returns 创建成功的 Memory 信息
 */
export const createMemory = async (params: CreateMemoryParams) => {
  return request<BiographyMemoryItem>({
    url: '/v1/memories',
    method: 'POST',
    body: params
  })
}

/**
 * 删除 Memory
 * @param memoryId Memory ID
 * @returns 删除结果
 */
export const deleteMemory = async (memoryId: number) => {
  return request<void>({
    url: `/v1/memories/${memoryId}`,
    method: 'DELETE'
  })
}

// 更新 Memory 参数接口
export interface UpdateMemoryParams {
  brief: string // 简要描述
  content: string // 内容
}

/**
 * 更新 Memory
 * @param memoryId Memory ID
 * @param params 更新参数
 * @returns 更新成功的 Memory 信息
 */
export const updateMemory = async (memoryId: number, params: UpdateMemoryParams) => {
  return request<BiographyMemoryItem>({
    url: `/v1/memories/${memoryId}`,
    method: 'PUT',
    body: params
  })
}
